<template>
  <v-row no-gutters>
    <v-col>
      <v-row no-gutters class="pr-2" :class="{ 'no-print': isQuickPrint }" v-if="computedLayers.length > 0">
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn
            small
            icon
            @click="changeAllLabelsVisibility"
            :title="
              areAllLabelsVisible
                ? $i18n.t('dialog.style.toggleOffAllLabels')
                : $i18n.t('dialog.style.toggleOnAllLabels')
            "
          >
            <v-icon
              color="font"
              style="height: 19px; width: 19px"
              v-text="areAllLabelsVisible ? '$labelActive' : '$label'"
            ></v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <draggable-treeview
        :items.sync="computedLayers"
        item-children="layers"
        :selectable="!isQuickPrint"
        selectable-key="visible"
        group-name-key="name"
        item-name-key="name"
        not-selectable-children-key="has_permission"
        @input="updateLayersOrder"
        @select="changeLayerVisibility"
        :expand-icon="!isQuickPrint ? 'mdi-menu-down' : ''"
      >
        <template #prepend="{ item }">
          <v-col cols="auto" class="pa-0" v-if="!item.styles">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="pa-0 pt-1" v-if="!item.layers">
                <img
                  v-if="item.style && item.style.externalGraphic"
                  width="20px"
                  height="20px"
                  :src="item.iconSrc"
                  class="ml-1 mt-1"
                />
                <legend-layer-list-icon v-else :color="item.color" :layer-type="item.layerType" />
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #label="{ item }">
          <v-col cols="auto" class="pa-0 pl-1">
            <v-row no-gutters align="center">
              <v-col
                cols="auto"
                class="pa-0 pt-1 text-body-2"
                :class="{
                  'text--disabled': !item.has_permission && !item.layers,
                  'font-weight-bold': item.layers ? true : false,
                }"
              >
                <v-tooltip bottom color="font" :disabled="Boolean(item.layers)">
                  <template #activator="{ on }">
                    <span v-on="on" :class="{ 'text--disabled': checkLayerVisibility(item) }">
                      {{ item.name }}
                    </span>
                    <span v-on="on" class="text--disabled font-italic" v-if="item.cache">
                      {{ '(cache)' }}
                    </span>
                  </template>
                  {{
                    item.has_permission && !checkLayerVisibility(item)
                      ? item.name
                      : !item.has_permission
                      ? $i18n.t('sidebar.noLayerPermissions')
                      : checkLayerVisibility(item)
                      ? $i18n.t('sidebar.layerVisibility', { suffix: item.style.minzoom })
                      : item.name
                  }}
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row v-if="item.styles">
              <v-col class="pa-0 pl-2 pb-2">
                <legend-layers-list-styles
                  :default-style="item.style"
                  :styles="item.styles"
                  :layer-type="item.layerType"
                  :has-permission="item.has_permission"
                />
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #append="{ item }">
          <v-col cols="auto" class="pa-0" :class="{ 'no-print': isQuickPrint }">
            <v-row no-gutters align="center">
              <template v-if="item.isActionsVisible">
                <v-col cols="auto" v-for="(button, idx) of item.buttons" :key="idx">
                  <v-tooltip bottom color="font">
                    <template #activator="{ on: onTooltip }">
                      <v-btn small icon v-on="onTooltip" @click.native.stop="buttonAction(button.name, item)">
                        <v-icon v-bind="button.icon"> {{ button.icon.icon }} </v-icon>
                      </v-btn>
                    </template>
                    {{ button.tooltip }}
                  </v-tooltip>
                </v-col>
              </template>
              <v-col cols="auto">
                <dots-menu
                  v-if="item.hasDotsMenus"
                  :items="item.dotsMenus"
                  @delete="showLayerDeleteDialog(item)"
                  @changeStyle="openLayerStyle(item)"
                  @refresh="refreshProjectLayer(item.layer_id)"
                  @deleteGroup="showGroupDeleteDialog(item)"
                  :close-on-content-click="false"
                >
                  <template #transparency>
                    <v-row no-gutters align="center" justify="center" style="margin-top:-10px; height: 30px">
                      <v-col cols="10">
                        <v-slider
                          @change="setLayerOpacity(item.layer_id, $event)"
                          :value="item.opacity"
                          step="0.01"
                          min="0"
                          max="1"
                          hide-details
                        ></v-slider>
                      </v-col>
                    </v-row>
                  </template>
                </dots-menu>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </draggable-treeview>
      <v-list-item
        class="pa-0 pb-1 pl-8"
        :class="{ 'no-print': isQuickPrint }"
        style="cursor: pointer; min-height: 30px"
        v-if="isOsmItemVisible"
      >
        <v-list-item-action style="margin: 0 5px 0 0; align-self: start">
          <v-row no-gutters class="align-center">
            <v-col cols="6" style="margin: 0">
              <v-checkbox v-model="isOpenStreetMapVisible" @change="toggleOpenStreetMap" color="primary"> </v-checkbox>
            </v-col>
          </v-row>
        </v-list-item-action>
        <v-list-item-content>
          <v-col class="py-0">
            <v-row style="min-height: 27px">
              <v-list-item-title style="text-align: left" class="text-body-2" v-text="'OpenStreetMap'" />
            </v-row>
          </v-col>
        </v-list-item-content>
      </v-list-item>
    </v-col>
    <main-dialog
      v-if="isLayerDeleteDialogLoaded"
      :is-visible.sync="isLayerDeleteDialogVisible"
      :title="$i18n.t('dialog.deleteLayer')"
      :body="dialogBodyText"
      agree-text="dialog.agreeDelete"
      @agree="deleteLayerFromProject"
    />
    <main-dialog
      v-if="isGroupDeleteDialogLoaded"
      :is-visible.sync="isGroupDeleteDialogVisible"
      :title="$i18n.t('dialog.deleteGroup')"
      :body="
        $i18n.t(
          groupToDelete && groupToDelete.layers.length
            ? 'dialog.confirmGroupDeleteWithLayers'
            : 'dialog.confirmGroupDelete',
          {
            name: groupToDelete ? groupToDelete.name : '',
          }
        )
      "
      agree-text="dialog.agreeDelete"
      ref="deleteGroupDialog"
    >
      <template #buttons>
        <v-spacer></v-spacer>
        <template v-if="groupToDelete && groupToDelete.layers.length">
          <v-col cols="auto">
            <button-main-dialog @click="deleteGroupFromProject(true)" :text="$i18n.t('dialog.agreeYes')" />
          </v-col>
          <v-col cols="auto">
            <button-main-dialog cancel @click="deleteGroupFromProject(false)" :text="$i18n.t('dialog.disagreeNo')" />
          </v-col>
        </template>
        <template v-else>
          <v-col cols="auto">
            <button-main-dialog @click="deleteGroupFromProject(true)" :text="$i18n.t('dialog.agreeDelete')" />
          </v-col>
        </template>
        <v-col cols="auto">
          <button-main-dialog cancel @click="$refs.deleteGroupDialog.onClose()" :text="$i18n.t('dialog.disagree')" />
        </v-col>
      </template>
    </main-dialog>
    <style-dialog
      v-if="isStyleDialogVisible"
      :layer="currentLayerStyleEdit"
      :attributes="currentLayerStyleEditAttributes"
      :geomType="currentLayerStyleEditGeometryType"
      :is-visible.sync="isStyleDialogVisible"
      @acceptStyle="setLayerStyle"
    />
  </v-row>
</template>

<script>
import { get, call, sync } from 'vuex-pathify';

import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';

import DotsMenu from '@/components/DotsMenu';
import LegendLayerListIcon from '@/components/LegendLayerListIcon';
import LegendLayersListStyles from '@/components/LegendLayersListStyles';
import DraggableTreeview from '@/components/DraggableTreeview';
import ButtonMainDialog from '@/components/ButtonMainDialog';

export default {
  name: 'LegendLayersList',
  mixins: [fetchAttributesSchemaMixin],
  components: {
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    StyleDialog: () => import('@/components/StyleDialog'),
    LegendLayerListIcon,
    LegendLayersListStyles,
    DraggableTreeview,
    ButtonMainDialog,
  },
  model: {
    prop: 'layers',
  },
  props: {
    layers: {
      type: Array,
      default: () => [],
    },
    isOsmItemVisible: {
      type: Boolean,
      deafult: false,
    },
    isManipulationDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentLayerStyleEdit: {},
    currentLayerStyleEditAttributes: [],
    currentLayerStyleEditGeometryType: '',
    isLayerDeleteDialogVisible: false,
    isLayerDeleteDialogLoaded: false,
    isGroupDeleteDialogVisible: false,
    isGroupDeleteDialogLoaded: false,
    layerToDelete: undefined,
    groupToDelete: undefined,
    isStyleDialogVisible: false,
    isTransparency: false,
    expandedLayers: {},
  }),
  computed: {
    mapPanelDataFetched: sync('map/mapPanelDataFetched'),
    layersMetadata: get('layers/metadata'),
    isOpenStreetMapVisible: sync('map/isOpenStreetMapVisible'),
    isModifySnappingActive: get('edit/isModifySnappingActive'),
    zoom: get('map/zoom'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    layersList() {
      return this.$_getFlatGroupsLayers(this.layers).layers;
    },
    currentLayerId() {
      return this.$route.params.lid;
    },
    dialogBodyText() {
      if (!this.layerToDelete) {
        return;
      }
      const { name } = this.layerToDelete;
      return this.$i18n.t('dialog.confirmLayerDelete', { name });
    },
    computedLayers: {
      get() {
        return JSON.parse(JSON.stringify(this.layers || [])).map(element => {
          if (element.layers) {
            element.layers = element.layers.map(layer => {
              return this.getLegendLayer(layer);
            });
            return this.getLegendGroup(element);
          } else {
            return this.getLegendLayer(element);
          }
        });
      },
      set(nV) {
        let elements = JSON.parse(JSON.stringify(nV));
        elements.map(element => {
          if (element.layers) {
            element.layers = element.layers.map(layer => {
              return this.setLegendLayer(layer);
            });
            return this.setLegendGroup(element);
          } else {
            return this.setLegendLayer(element);
          }
        });
        this.$emit('input', elements);
      },
    },
    areAllLabelsVisible() {
      return this.layersList
        .filter(layer => layer.type === 'features_layer' && layer.has_permission)
        .every(layer => layer.labels_visible);
    },
  },
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getLegendGroup(group) {
      const selectableDisabled =
        !group.layers.find(layer => layer.has_permission) || this.isModifySnappingActive || group.layers.length < 1;
      return {
        ...group,
        isActionsVisible: group.layers.find(layer => layer.type === 'features_layer' && layer.has_permission),
        dotsMenus: this.isManipulationDisabled
          ? []
          : [
              {
                name: 'deleteGroup',
              },
            ],
        hasDotsMenus: !this.isManipulationDisabled,
        selectableTooltip: this.isModifySnappingActive
          ? this.$i18n.t('button.toChangeGroupVisibility', { reason: this.$i18n.t('button.turnOffEditing') })
          : group.layers.length > 0
          ? group.layers.filter(layer => layer.has_permission).length > 0
            ? this.$i18n.t('button.groupVisbility', {
                onOff: this.$i18n.t(
                  `button.${
                    group.layers.filter(layer => layer.has_permission).every(layer => layer.visible) ? 'off' : 'on'
                  }`
                ),
              })
            : this.$i18n.t('button.changeGroupVisibilityNoPermission')
          : this.$i18n.t('button.changeGroupVisibilityNoLayers'),
        selectableDisabled,
        buttons: group.layers.find(layer => layer.type === 'features_layer' && layer.has_permission)
          ? [
              {
                name: 'changeLabelsVisibility',
                icon: {
                  color: 'font',
                  style: 'height: 19px; width: 19px',
                  icon: group.layers
                    .filter(layer => layer.type === 'features_layer' && layer.has_permission)
                    .every(layer => layer.labels_visible)
                    ? '$labelActive'
                    : '$label',
                },
                tooltip: group.layers
                  .filter(layer => layer.type === 'features_layer' && layer.has_permission)
                  .every(layer => layer.labels_visible)
                  ? this.$i18n.t('button.turnOffLabels')
                  : this.$i18n.t('button.turnOnLabels'),
              },
            ]
          : [],
      };
    },
    getLegendLayer(layer) {
      const dotsMenus = this.dotsMenuItems(
        layer.type,
        layer.geometry_type,
        layer.supports_mvt,
        layer.has_permission,
        this.isManipulationDisabled
      );
      return {
        ...layer,
        iconSrc: this.getIcon(layer.style ? layer.style.externalGraphic : null),
        color: this.getIconColor(layer.style),
        layerType: this.getLayerType(layer),
        styles: this.getLayerStylesHandler(layer),
        visible: this.getIsVisible(layer),
        isActionsVisible: layer.type === 'features_layer' && layer.has_permission,
        dotsMenus,
        hasDotsMenus: dotsMenus?.length > 0,
        isExpaned: this.expandedLayers[layer.layer_id],
        selectableDisabled: !layer.has_permission || layer.geometry_type === null || this.isModifySnappingActive,
        selectableTooltip: this.getChangeVisibilityTooltip(
          layer.visible,
          layer.geometry_type,
          layer.has_permission,
          this.isModifySnappingActive
        ),
        buttons:
          layer.type === 'features_layer' && layer.has_permission
            ? [
                ...(layer.geometry_type
                  ? [
                      {
                        name: 'changeLabelsVisibility',
                        icon: {
                          color: 'font',
                          style: 'height: 19px; width: 19px',
                          icon: layer.labels_visible ? '$labelActive' : '$label',
                        },
                        tooltip: layer.labels_visible
                          ? this.$i18n.t('button.turnOffLabels')
                          : this.$i18n.t('button.turnOnLabels'),
                      },
                      ...(layer.geometry_type === 'linestring' || layer.geometry_type === 'multilinestring'
                        ? [
                            {
                              name: 'changeLineArrowsVisibility',
                              icon: {
                                color: layer.direction_arrows_visible ? 'link' : 'rgba(0,0,0,0.7)',
                                style: 'font-size: 1.3rem',
                                icon: 'mdi-axis-arrow',
                              },
                              tooltip: layer.direction_arrows_visible
                                ? this.$i18n.t('button.turnOffDirectionArrows')
                                : this.$i18n.t('button.turnOnDirectionArrows'),
                            },
                          ]
                        : []),
                      {
                        name: 'zoomToLayer',
                        icon: { color: 'rgba(0,0,0,0.7)', style: 'height: auto', icon: 'mdi-fullscreen' },
                        tooltip: this.$i18n.t('button.zoomToLayer'),
                      },
                    ]
                  : []),
                {
                  name: 'setLayer',
                  icon: {
                    color: this.currentLayerId == layer.layer_id ? 'link' : 'rgba(0,0,0,0.7)',
                    style: 'height: auto',
                    icon: 'mdi-table-large',
                  },
                  tooltip:
                    this.currentLayerId == layer.layer_id
                      ? this.$i18n.t('button.closeTable')
                      : this.$i18n.t('button.openTable'),
                },
              ]
            : [],
      };
    },
    setLegendGroup(group) {
      const list = [
        'isActionsVisible',
        'dotsMenus',
        'hasDotsMenus',
        'buttons',
        'selectableDisabled',
        'selectableTooltip',
      ];
      for (const l of list) {
        delete group[l];
      }
      return group;
    },
    setLegendLayer(layer) {
      const list = [
        'iconSrc',
        'color',
        'layerType',
        'styles',
        'isActionsVisible',
        'dotsMenus',
        'hasDotsMenus',
        'isExpaned',
        'selectableDisabled',
        'selectableTooltip',
        'buttons',
      ];
      for (const l of list) {
        delete layer[l];
      }
      return layer;
    },
    buttonAction(name, item) {
      this[name](item.layers ? 'group' : 'layer', item);
    },
    getChangeVisibilityTooltip(isVisible, geometryType, hasPermission, isModifySnappingActive) {
      return isModifySnappingActive
        ? this.$i18n.t('button.toChangeLayerVisibility', { reason: this.$i18n.t('button.turnOffEditing') })
        : geometryType === null
        ? this.$i18n.t('button.layerWithoutGeometry')
        : hasPermission
        ? this.$i18n.t('button.layerVisbility', { onOff: this.$i18n.t(`button.${isVisible ? 'off' : 'on'}`) })
        : this.$i18n.t('button.changeLayerVisibilityNoPermission');
    },
    getLayerStylesHandler(layer) {
      if (!layer.style || (!layer.style.uniques && !layer.style.ranges)) {
        return;
      }
      const styles = layer.style.uniques ? this.$_objectToArray(layer.style.uniques.values) : layer.style.ranges.values;
      return this.getLayerStyles(styles);
    },
    getIsVisible(layer) {
      return layer.has_permission && layer.visible && layer.geometry_type !== null;
    },
    dotsMenuItems(type, geomType, supportsMvt, hasPermission, isManipulationDisabled) {
      const items = [
        ...(geomType !== null
          ? [
              {
                name: 'transparency',
                isVisible: hasPermission,
              },
            ]
          : []),
        {
          name: 'changeStyle',
          type: 'features_layer',
          isVisible: hasPermission && type === 'features_layer' && geomType && !isManipulationDisabled,
          disabled: !this.mapPanelDataFetched,
          tooltip: this.mapPanelDataFetched ? '' : this.$i18n.t('default.downloadingDataInProgress'),
        },
        {
          name: 'refresh',
          type: 'features_layer',
          isVisible: hasPermission && type === 'features_layer' && supportsMvt,
        },
        {
          name: 'delete',
          isVisible: !isManipulationDisabled,
        },
      ];
      return items.filter(item => item.isVisible);
    },
    refreshProjectLayer(layerId) {
      this.$root.$emit('refreshMvtSource', layerId);
    },
    showLayerDeleteDialog(layer) {
      this.layerToDelete = layer;
      this.isLayerDeleteDialogLoaded = true;
      this.isLayerDeleteDialogVisible = true;
    },
    showGroupDeleteDialog(group) {
      this.groupToDelete = group;
      this.isGroupDeleteDialogLoaded = true;
      this.isGroupDeleteDialogVisible = true;
    },
    changeLayerVisibility(e) {
      if (e.type === 'group') {
        const group = this.layers[e.itemIdx];
        for (const layer of group.layers.filter(layer => layer.has_permission)) {
          this.$root.$emit('toggleProjectLayerVisible', layer.layer_id, e.value);
        }
      } else {
        if (e.parentItemIdx || e.parentItemIdx === 0) {
          const item = this.layers[e.parentItemIdx].layers[e.itemIdx];
          this.$root.$emit('toggleProjectLayerVisible', item.layer_id, e.value);
        } else {
          const item = this.layers[e.itemIdx];
          this.$root.$emit('toggleProjectLayerVisible', item.layer_id, e.value);
        }
      }
      this.$emit('hasUnsavedChanges', true);
    },
    changeAllLabelsVisibility() {
      const visible = !this.areAllLabelsVisible;
      this.$store.set('layers/SET_ALL_LAYERS_LABELS_VISIBILITY!', visible);
      this.$emit('hasUnsavedChanges', true);
      this.$root.$emit('toggleProjectAllLayersLabels', visible);
    },
    changeLabelsVisibility(type, item) {
      if (type === 'group') {
        const layers = item.layers.filter(layer => layer.type === 'features_layer' && layer.has_permission);
        const currentVisible = layers.every(layer => layer.labels_visible);
        for (const layer of layers) {
          this.$store.set('layers/SET_LAYER_LABELS_VISIBILITY!', { layerId: layer.layer_id, visible: !currentVisible });
          this.$root.$emit('toggleProjectLayerLabels', layer.layer_id, !currentVisible);
        }
        this.$emit('hasUnsavedChanges', true);
        return;
      }
      this.$store.set('layers/SET_LAYER_LABELS_VISIBILITY!', { layerId: item.layer_id, visible: !item.labels_visible });
      this.$emit('hasUnsavedChanges', true);
      this.$root.$emit('toggleProjectLayerLabels', item.layer_id, !item.labels_visible);
    },
    changeLineArrowsVisibility(type, item) {
      this.$store.set('layers/SET_LAYER_ARROWS_VISIBILITY!', {
        layerId: item.layer_id,
        visible: item.direction_arrows_visible ? false : true,
      });
      this.$emit('hasUnsavedChanges', true);
      this.$root.$emit(
        'toggleProjectLayerDirectionArrows',
        item.layer_id,
        item.direction_arrows_visible ? false : true
      );
    },
    deleteLayerFromProject() {
      const layerId = this.layerToDelete.layer_id;
      this.$store.set('layers/DELETE_PROJECT_LAYER!', layerId);
      this.$root.$emit('deleteProjectLayer', layerId);
      this.$emit('hasUnsavedChanges', true);
      if (this.currentLayerId == layerId) {
        this.$router.push({ name: 'map' });
      }
      this.isLayerDeleteDialogVisible = false;
    },
    deleteGroupFromProject(deleteLayers) {
      const layers = JSON.parse(JSON.stringify(this.groupToDelete.layers));
      const stringifyGroup = JSON.stringify({
        name: this.groupToDelete.name,
        layers: this.groupToDelete.layers.map(layer => {
          return { id: layer.layer_id };
        }),
      });
      const index = this.layers.findIndex(group => {
        const { name, layers } = group;
        return (
          JSON.stringify({
            name,
            layers: (layers || []).map(layer => {
              return { id: layer.layer_id };
            }),
          }) === stringifyGroup
        );
      });
      if (index < 0) {
        this.$refs?.deleteGroupDialog?.onClose();
        return;
      }
      this.$store.set('layers/DELETE_PROJECT_GROUP!', index);
      for (const layer of layers) {
        const layerId = layer.layer_id;
        this.$root.$emit('deleteProjectLayer', layerId);
      }
      if (!deleteLayers) {
        this.$store.set('layers/ADD_PROJECT_LAYERS!', layers);
        this.$root.$emit('pushProjectLayers', layers);
      }
      this.$emit('hasUnsavedChanges', true);
      this.$refs?.deleteGroupDialog?.onClose();
    },
    updateLayersOrder() {
      this.$emit('hasUnsavedChanges', true);
      this.$root.$emit('setProjectLayersOrder');
    },
    setLayerOpacity(layerId, value) {
      this.$store.set('layers/SET_LAYER_OPACITY!', { layerId, opacity: value });
      this.$emit('hasUnsavedChanges', true);
      this.$root.$emit('setProjectLayerOpacity', layerId, value);
    },
    getLayerStyles(stylesValues) {
      return stylesValues.map(value => {
        return {
          ...value,
          key: value.label || value.label === '' ? value.label : value.value,
          color: this.getIconColor(value),
          icon: value.externalGraphic && this.getIcon(value.externalGraphic),
        };
      });
    },
    setLayer(type, item) {
      if (type === 'group') {
        return;
      }
      const customTableRoutesNamesToggleOn = {
        locationLabReports: 'locationLabReportsLayer',
        locationLabReportsLayer: 'locationLabReportsLayer',
        zdmCustomReports: 'zdmCustomReportsLayer',
        zdmCustomReportsLayer: 'zdmCustomReportsLayer',
        fireSimulation: 'fireSimulationLayer',
        fireSimulationLayer: 'fireSimulationLayer',
      };
      const customTableRoutesNamesToggleOff = {
        locationLabReports: 'locationLabReports',
        locationLabReportsLayer: 'locationLabReports',
        zdmCustomReports: 'zdmCustomReports',
        zdmCustomReportsLayer: 'zdmCustomReports',
        fireSimulation: 'fireSimulation',
        fireSimulationLayer: 'fireSimulation',
      };
      const customTableRoutesNamesKeysOn = Object.keys(customTableRoutesNamesToggleOn);
      const customTableRoutesNamesKeysOff = Object.keys(customTableRoutesNamesToggleOff);
      const layerId = item.layer_id;
      if (this.$route.params.lid == layerId) {
        this.$router.push({
          name: customTableRoutesNamesKeysOff.includes(this.$route.name)
            ? customTableRoutesNamesToggleOff[this.$route.name]
            : 'map',
        });
      } else {
        this.$router.push({
          name: customTableRoutesNamesKeysOn.includes(this.$route.name)
            ? customTableRoutesNamesToggleOn[this.$route.name]
            : 'layerTable',
          params: { lid: layerId },
        });
      }
    },
    setLayerStyle(layer) {
      const layerId = layer.layer_id;
      const style = layer.style;
      delete layer.style.symbolizationKind;
      this.$store.set('layers/SET_LAYER_STYLE!', { layerId, style });
      this.$root.$emit('setProjectLayerStyle', layerId);
      this.$emit('hasUnsavedChanges', true);
      this.isStyleDialogVisible = false;
    },
    async openLayerStyle(layer) {
      this.currentLayerStyleEditGeometryType = layer.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(JSON.stringify(layer));
      const currentLayerMetadata = this.layersMetadata[layer.data_source_name].attributes_schema.attributes;
      this.currentLayerStyleEditAttributes = this.getAttributes(layer.form_schema.elements).map(a => {
        const isRelational = Boolean(currentLayerMetadata.find(attr => attr.name === a.attribute).relation);
        const dataType = currentLayerMetadata.find(attr => attr.name === a.attribute).data_type;
        const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
        return {
          isRelational,
          valueLabel: isRelational ? `mvt_rel_${a.attribute}` : a.attribute,
          valueMvt: isDate ? `mvt_int_${a.attribute}` : a.attribute,
          value: a.attribute,
          text: a.label,
          dataType,
        };
      });
      this.isStyleDialogVisible = true;
    },
    getAttributes(elements) {
      return elements.reduce((total, current) => {
        if (current.element_type === 'group') {
          return [...total, ...this.getAttributes(current.elements)];
        }
        return [...total, current];
      }, []);
    },
    getLayerType(layer) {
      return layer.type === 'features_layer' ? layer.geometry_type : layer.service_type;
    },
    getIconColor(style) {
      if (style) {
        return style['fill-color'] || style['circle-color'] || style['line-color'] || 'green';
      } else {
        return 'grey';
      }
    },
    getIcon(name) {
      return `${process.env.VUE_APP_API_URL}/icons/${name}`;
    },
    toggleOpenStreetMap(value) {
      this.$root.$emit('toggleOpenStreetMap', value);
    },
    async zoomToLayer(type, item) {
      if (type === 'group') {
        return;
      }
      const r = await this.getLayerFeatures({
        layer_id: item.layer_id,
        params: {
          with_features: false,
          with_collection_bbox: true,
          with_count: false,
          with_geometry: false,
        },
      });
      if (r.data.data.bbox) {
        this.$root.$emit('fitView', r.data.data.bbox);
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.zoomToEmptyLayer'),
        });
      }
    },
    checkLayerVisibility(layer) {
      if (layer.style?.uniques || layer.style?.ranges) {
        return;
      } else {
        return layer.style?.minzoom > this.zoom;
      }
    },
  },
};
</script>
